/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, Image, NativeFileInput } from '@/shared/ui';
import { useAgencyLogoUpload } from '../../hooks/use-agency-logo-upload';

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';

interface LogoCardProps {
  agencyId: string;
  logo?: string;
}

/**
 * Renders the visual identity card to upload, replace, or remove the logo.
 *
 * @param props - The logo card props.
 * @param props.agencyId - The identifier of the agency.
 * @param props.logo - The current logo URL, if any.
 * @returns The logo card element.
 */
export function LogoCard({ agencyId, logo }: LogoCardProps) {
  const { t } = useTranslation('features/agency-settings');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, remove, status, error } = useAgencyLogoUpload(agencyId);
  const busy = status === 'converting' || status === 'uploading';

  const openPicker = () => fileInputRef.current?.click();

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) upload(file);
    event.target.value = '';
  };

  // No card and no title of its own: this now sits inside a settings block
  // that already draws both. Keeping them nested a card inside a card and
  // printed the heading twice, and cost the Perfil tab a screenful of height.
  return (
    <div>
      {/* eslint-disable-next-line no-restricted-syntax -- hidden programmatic file picker has no design-system equivalent; mirrors DocumentsTab precedent */}
      <NativeFileInput
        ref={fileInputRef}
        
        accept={LOGO_ACCEPT}
        onChange={onFileSelected}
        style={{ display: 'none' }}
        data-testid="logo-file-input"
      />
      {logo ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <Image
            src={logo}
            alt={t('v2.logo.alt')}
            style={{ width: 80, height: 80, borderRadius: 'var(--radius-md)', objectFit: 'cover', border: '1px solid var(--border-subtle)' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <Button
              variant="none"
              disableSize
              onClick={openPicker}
              disabled={busy}
              style={{
                fontSize: 'var(--text-body-sm)',
                fontWeight: 600,
                color: 'var(--seed-base)',
                background: 'transparent',
                border: '1px solid var(--seed-base)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-2) var(--space-4)',
                cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {busy ? t('v2.logo.uploading') : t('v2.logo.changeLogo')}
            </Button>
            <Button
              variant="none"
              disableSize
              onClick={() => remove()}
              disabled={busy}
              style={{
                fontSize: 'var(--text-body-sm)',
                fontWeight: 600,
                color: 'var(--status-error)',
                background: 'transparent',
                border: 'none',
                cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {t('v2.logo.removeLogo')}
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={openPicker}
          role="button"
          tabIndex={0}
          style={{
            border: '2px dashed var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-4)',
            textAlign: 'center',
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          <div style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
            {busy ? t('v2.logo.uploading') : (
              <>{t('v2.logo.dragPrefix')} <Text component="span" style={{ color: 'var(--seed-base)' }}>{t('v2.logo.clickToUpload')}</Text></>
            )}
          </div>
          <div style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>{t('v2.logo.formatLimit')}</div>
        </div>
      )}
      {status === 'error' && error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-body-sm)', color: 'var(--status-error)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
